import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical, type LucideIcon } from 'lucide-react';
import { getDropdownPositionRightAligned } from '../../utils/dropdownPosition';

export type RowActionVariant = 'default' | 'primary' | 'destructive';

export interface RowActionItem {
  id: string;
  label: string;
  icon: LucideIcon;
  variant?: RowActionVariant;
  /** Render label beside icon for compact inline buttons (e.g. Post, Pay). */
  showLabel?: boolean;
  onClick: () => void;
}

export type RowMenuEntry = RowActionItem | { type: 'separator' };

const isMenuSeparator = (entry: RowMenuEntry): entry is { type: 'separator' } =>
  'type' in entry && entry.type === 'separator';

interface RowActionsMenuProps {
  inlineActions: RowActionItem[];
  menuEntries: RowMenuEntry[];
  disabled?: boolean;
}

const GREEN_PRIMARY_PILL =
  'inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded';
const SLATE_PRIMARY_PILL =
  'inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-700 bg-slate-50 hover:bg-slate-100 rounded';

const inlineButtonClass = (action: RowActionItem) => {
  if (action.showLabel || action.variant === 'primary') {
    if (action.id === 'post' || action.id === 'receive' || action.id === 'convert_to_invoice') {
      return GREEN_PRIMARY_PILL;
    }
    if (action.id === 'receive_payment' || action.id === 'pay_bill') {
      return SLATE_PRIMARY_PILL;
    }
    return SLATE_PRIMARY_PILL;
  }

  if (action.variant === 'destructive') {
    return 'p-1.5 text-red-600 hover:bg-red-50 rounded';
  }

  return 'p-1.5 text-slate-600 hover:bg-slate-50 rounded';
};

const menuItemClass = (variant: RowActionVariant = 'default') => {
  if (variant === 'destructive') {
    return 'w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-red-600 hover:bg-red-50';
  }
  return 'w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-gray-800 hover:bg-gray-50';
};

const RowActionsMenu = ({ inlineActions, menuEntries, disabled = false }: RowActionsMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const menuItems = menuEntries.filter(
    (entry): entry is RowActionItem => !isMenuSeparator(entry)
  );
  const showMenu = menuItems.length > 0;

  const updateDropdownPosition = useCallback(() => {
    if (!triggerRef.current) return;
    setDropdownStyle(getDropdownPositionRightAligned(triggerRef.current.getBoundingClientRect()));
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    updateDropdownPosition();
    requestAnimationFrame(updateDropdownPosition);

    const handleReposition = () => updateDropdownPosition();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [isOpen, updateDropdownPosition]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const runAction = (action: RowActionItem) => {
    setIsOpen(false);
    action.onClick();
  };

  const dropdown =
    isOpen && showMenu ? (
      <div
        ref={dropdownRef}
        style={dropdownStyle}
        className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden min-w-[200px]"
      >
        <ul className="max-h-72 overflow-y-auto py-1">
          {menuEntries.map((entry, index) => {
            if (isMenuSeparator(entry)) {
              return <li key={`sep-${index}`} className="my-1 border-t border-gray-100" aria-hidden />;
            }

            const Icon = entry.icon;
            return (
              <li key={entry.id}>
                <button
                  type="button"
                  onClick={() => runAction(entry)}
                  disabled={disabled}
                  className={`${menuItemClass(entry.variant)} disabled:opacity-50`}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span>{entry.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    ) : null;

  if (inlineActions.length === 0 && !showMenu) {
    return null;
  }

  return (
    <div ref={containerRef} className="flex justify-center items-center gap-1">
      {inlineActions.map((action) => {
        const Icon = action.icon;
        const labeled = action.showLabel || action.variant === 'primary';
        return (
          <button
            key={action.id}
            type="button"
            onClick={() => action.onClick()}
            disabled={disabled}
            className={`${inlineButtonClass(action)} disabled:opacity-50`}
            title={action.label}
          >
            <Icon className={labeled ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
            {labeled ? <span>{action.label}</span> : null}
          </button>
        );
      })}

      {showMenu && (
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setIsOpen((open) => !open)}
          disabled={disabled}
          className={`p-1.5 rounded ${
            isOpen ? 'bg-slate-100 text-slate-800' : 'text-slate-600 hover:bg-slate-50'
          } disabled:opacity-50`}
          title="More actions"
          aria-label="More actions"
          aria-expanded={isOpen}
        >
          <MoreVertical className="h-4 w-4" />
        </button>
      )}

      {dropdown && createPortal(dropdown, document.body)}
    </div>
  );
};

export default RowActionsMenu;
